import React, {
  FC,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { ContactMessage, MessageDirection, setChatInterface } from 'models/twilio';
import { useTranslation } from 'react-i18next';
import useSendNameCard from 'components/chat/utils/useSendNameCard';
import {
  ButtonBase, Card, CardActions, CardContent, useMediaQuery,
} from '@material-ui/core';
import TRANSLATIONS from 'components/twilio/translations';
import { BEHAVIOR, COLORS } from 'components/twilio/constants';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Routes from 'Routes';
import { selectLocale } from 'models/localization';
import { nameByLocale } from 'hooks/useNameByLocale';
import UserAvatar from '../styled/UserAvatar';
import { ChatTitleText } from '../CommonChatDisplayComponents';

const StyledCard = styled(Card)`
  background-color: ${COLORS.WHITE};
  max-width: 300px;
  color: ${COLORS.TEXT_DARK};
  padding: 16px;
`;

const StyledCardContent = styled(CardContent)`
  display: flex;
  padding: 0;

  :last-child {
    padding-bottom: 0;
  }
`;

const CardEmailContainer = styled.a`
  text-decoration: none;
`;

const StyledCardActions = styled(CardActions)`
  padding: 16px 0 0 0;
`;

const CardDetailsContainer = styled.div`
  min-width: 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const CardSubtitle = styled.div<{ noWrap?: boolean }>`
  font-family: Lato, sans-serif;
  font-size: 12px;
  color: ${COLORS.TEXT_DARK};
  ${({ noWrap }) => noWrap && `
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `}
`;

const AddContactButton = styled(ButtonBase)`
  font-family: Lato, sans-serif;
  height: 24px;
  line-height: 24px;
  color: ${COLORS.WHITE};
  background-color: ${COLORS.SKY_BLUE};
  border-radius: 2px;
  font-size: 11px;
  width: 100%;
`;

export interface ContactCardProps {
  card: ContactMessage;
  messageDirection: MessageDirection;
}

const detectOverflow = <T extends HTMLElement>(element: T) => element?.offsetWidth < element?.scrollWidth;

const ContactCard: FC<ContactCardProps> = ({ card, messageDirection }) => {
  const {
    firstName,
    lastName,
    name,
    email,
    description,
    contactId,
  } = card;
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const localizedName = useMemo(() => (firstName && lastName ? nameByLocale(firstName, lastName, locale) : name), [firstName, lastName, locale, name]);

  const isMobile = useMediaQuery(`(max-width: ${BEHAVIOR.MOBILE_BREAKPOINT}px)`);
  const { t } = useTranslation();
  const [overflowing, setOverflowing] = useState(false);
  const emailRef = useRef<HTMLDivElement>();
  const contacts = useSelector((s) => s.contacts);
  const history = useHistory();
  const { handleAddToContact } = useSendNameCard();

  const existingContact = useMemo(() => contacts?.ids?.find((id) => id === contactId), [contacts, contactId]);

  const redirectToContacts = useCallback(() => {
    history.push(Routes.contacts);
    if (isMobile) dispatch(setChatInterface(false));
  }, [dispatch, isMobile, history]);

  const addToContact = useCallback(() => handleAddToContact(contactId), [handleAddToContact, contactId]);

  const addContactButtonProps = useMemo(() => ({
    onClick: existingContact ? redirectToContacts : addToContact,
    buttonText: existingContact ? t(...TRANSLATIONS.CONTACT.VIEW) : t(...TRANSLATIONS.CONTACT.ADD),
  }), [existingContact, t, redirectToContacts, addToContact]);

  const overflowCheck = useCallback(() => {
    if (emailRef.current) {
      const overflow = detectOverflow(emailRef.current);
      setOverflowing(overflow);
    }
  }, [emailRef]);

  useLayoutEffect(() => {
    setTimeout(overflowCheck, BEHAVIOR.CHAT_ANIMATION_TIMEOUT);
  }, [overflowCheck]);

  if (!card) return null;
  return (
    <Card>
      <StyledCard>
        <StyledCardContent>
          <UserAvatar name={localizedName} marginRight={12} />
          <CardDetailsContainer>
            <ChatTitleText>{localizedName}</ChatTitleText>
            {email && (
            <CardEmailContainer href={`mailto:${email}`}>
              {overflowing ? (
                <>
                  <CardSubtitle noWrap>{email.split('@')[0]}</CardSubtitle>
                  <CardSubtitle noWrap>{`@${email.split('@')[1]}`}</CardSubtitle>
                </>
              ) : <CardSubtitle ref={emailRef} noWrap>{email}</CardSubtitle>}
            </CardEmailContainer>
            )}
            {description && <CardSubtitle>{description}</CardSubtitle>}
          </CardDetailsContainer>
        </StyledCardContent>
        {messageDirection === MessageDirection.INCOMING && (
          <StyledCardActions>
            <AddContactButton onClick={addContactButtonProps.onClick}>
              {addContactButtonProps.buttonText}
            </AddContactButton>
          </StyledCardActions>
        )}
      </StyledCard>
    </Card>
  );
};

export default ContactCard;
